import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Link2, CheckCircle2, AlertTriangle, ExternalLink, Copy, Trash2 } from "lucide-react";

const cfg = { withCredentials: true };

function StatusRow({ ok, label, hint }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /> : <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />}
      <div><span className="font-medium">{label}</span>{hint && <span className="text-muted-foreground"> — {hint}</span>}</div>
    </div>
  );
}

function UnmatchedRow({ row, guardians, onMatched }) {
  const [gid, setGid] = useState("");
  const [busy, setBusy] = useState(false);
  const match = async () => {
    if (!gid) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/fonzip/donations/${row.fonzip_id}/match`, { guardian_id: gid }, cfg);
      toast.success("Eşleştirildi");
      onMatched();
    } catch (e) { toast.error(e.response?.data?.detail || "Eşleştirme başarısız"); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid md:grid-cols-[1fr_auto] gap-3 p-3 rounded-lg border bg-amber-50/40" data-testid={`fonzip-unmatched-${row.fonzip_id}`}>
      <div className="text-sm">
        <div className="font-semibold">{row.donor_name || "—"} <span className="text-muted-foreground">· {row.donor_email || "e-posta yok"}</span></div>
        <div className="text-muted-foreground">{Number(row.amount || 0).toLocaleString("tr-TR")} {row.currency} · Fonzip #{row.fonzip_id} · {row.recurring_status === 1 ? "Düzenli" : "Tek seferlik"} · {row.complete_date?.slice(0, 10)}</div>
      </div>
      <div className="flex gap-2 items-center">
        <select className="h-9 rounded-md border px-2 text-sm bg-white" value={gid} onChange={(e) => setGid(e.target.value)} data-testid={`fonzip-match-select-${row.fonzip_id}`}>
          <option value="">Koruyucu Aile seç…</option>
          {guardians.map((g) => <option key={g.guardian_id} value={g.guardian_id}>{g.guardian_name} → {g.dog_name} ({g.status})</option>)}
        </select>
        <Button size="sm" disabled={!gid || busy} onClick={match} data-testid={`fonzip-match-btn-${row.fonzip_id}`}>Eşleştir</Button>
      </div>
    </div>
  );
}

export default function FonzipPanel() {
  const [info, setInfo] = useState(null);
  const [rows, setRows] = useState([]);
  const [guardians, setGuardians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [whUrl, setWhUrl] = useState("");
  const [manualToken, setManualToken] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, d, g] = await Promise.all([
        axios.get(`${API}/admin/fonzip/overview`, cfg),
        axios.get(`${API}/admin/fonzip/donations?limit=100`, cfg),
        axios.get(`${API}/guardians?status=`, cfg),
      ]);
      setInfo(o.data); setRows(d.data.items || []); setGuardians(Array.isArray(g.data) ? g.data : g.data.guardians || []);
      setWhUrl((prev) => prev || o.data?.webhook_url || "");
    } catch (e) { toast.error(e.response?.data?.detail || "Fonzip bilgileri yüklenemedi"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try { const r = await fn(); toast.success(typeof okMsg === "function" ? okMsg(r.data) : okMsg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "İşlem başarısız"); }
    finally { setBusy(""); }
  };

  const toggle = (key, val) => act(key, () => axios.put(`${API}/content`, { items: { [key]: { value: val ? "true" : "false" } } }, cfg), val ? "Fonzip akışı açıldı" : "Fonzip akışı kapatıldı");

  if (loading && !info) return <div className="p-6 text-muted-foreground" data-testid="fonzip-loading">Yükleniyor…</div>;
  const unmatched = rows.filter((r) => r.paid && !r.match?.matched);
  const matched = rows.filter((r) => r.match?.matched);

  return (
    <div className="space-y-6" data-testid="fonzip-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-xl">Fonzip Bağlantısı</h2>
          <p className="text-sm text-muted-foreground">Koruyucu Aile ve aylık bağışlar Fonzip kampanya sayfasında tahsil edilir; webhook ile KAHU'ya eşleşir.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="fonzip-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Yenile</Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card><CardContent className="p-5 space-y-3">
          <h2 className="font-semibold">Bağlantı Durumu</h2>
          <StatusRow ok={info?.configured} label="API anahtarı" hint={info?.configured ? "tanımlı" : ".env'de FONZIP_CLIENT_ID / SECRET eksik"} />
          <StatusRow ok={info?.api_ok} label="Fonzip API erişimi" hint={info?.api_ok ? (info.organization || "bağlı") : (info?.api_error || "bağlanamadı")} />
          <StatusRow ok={!!info?.campaign_id} label="Kampanya kimliği" hint={info?.campaign_id ? `Fonzip kampanya #${info.campaign_id} — yalnızca bu kampanyanın bağışları KAHU'ya işlenir` : "henüz çözümlenmedi (API erişimi gerekiyor)"} />
          <StatusRow
            ok={info?.webhook_installed || info?.webhook_manual_confirmed}
            label="Webhook"
            hint={info?.webhook_installed ? "Fonzip'te kurulu (API ile doğrulandı)"
              : info?.webhook_manual_confirmed ? "elle kuruldu olarak işaretlendi"
              : info?.api_ok ? "Fonzip'te henüz yok" : "doğrulanamadı — Fonzip API erişimi yok"}
          />
          <div className="flex items-center gap-2 text-xs text-muted-foreground break-all">
            <Link2 className="w-3 h-3" /> {info?.webhook_url}
            <button className="p-1 hover:text-foreground" onClick={() => navigator.clipboard.writeText(info?.webhook_url || "").catch(() => {})} data-testid="fonzip-copy-webhook"><Copy className="w-3 h-3" /></button>
          </div>
          {!info?.webhook_installed && !info?.webhook_manual_confirmed && (
            <div className="space-y-2 pt-1">
              <input
                className="w-full h-9 rounded-md border px-2 text-xs bg-white"
                value={whUrl}
                onChange={(e) => setWhUrl(e.target.value)}
                placeholder="https://www.kahu.com.tr/api/fonzip/webhook"
                data-testid="fonzip-webhook-url-input"
              />
              <Button size="sm" disabled={!info?.api_ok || busy === "wh"} onClick={() => act("wh", () => axios.post(`${API}/admin/fonzip/webhook/install`, { url: whUrl || undefined }, cfg), (d) => d.already_existed ? "Webhook zaten kurulu" : "Webhook Fonzip'e kuruldu")} data-testid="fonzip-install-webhook">
                Webhook'u Fonzip'te Kur
              </Button>
              {!info?.api_ok && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2 text-xs" data-testid="fonzip-manual-guide">
                  <p className="font-semibold text-amber-900">API kilitli — Fonzip panelinden elle ekle</p>
                  <ol className="list-decimal pl-4 space-y-1 text-amber-900/90">
                    <li>fonzip.com → Ayarlar → Entegrasyonlar → Webhooks → <b>Yeni Webhook</b></li>
                    <li>Ad: <b>KAHU Platform</b></li>
                    <li>URL: <b className="break-all">{whUrl || info?.webhook_url}</b></li>
                    <li>Kimlik doğrulama: <b>Açık</b> · Header <b>{info?.webhook_auth_header}</b></li>
                    <li>Token: <b className="break-all">{info?.webhook_token}</b></li>
                    <li>Olay: yalnızca <b>DONATION (Bağış)</b> seçili</li>
                  </ol>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(`URL: ${whUrl || info?.webhook_url}\n${info?.webhook_auth_header}: ${info?.webhook_token}\nEvent: DONATION`).catch(() => {})} data-testid="fonzip-copy-manual">
                      <Copy className="w-3 h-3 mr-1" /> Bilgileri kopyala
                    </Button>
                    <Button size="sm" disabled={busy === "cfm"} onClick={() => act("cfm", () => axios.post(`${API}/admin/fonzip/webhook/confirm-manual`, { confirmed: true }, cfg), "Webhook elle kuruldu olarak işaretlendi")} data-testid="fonzip-confirm-manual">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Fonzip'te elle kurdum
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {!info?.api_ok && (
            <div className="flex gap-2 pt-1">
              <input
                className="flex-1 h-9 rounded-md border px-2 text-xs bg-white"
                value={manualToken}
                onChange={(e) => setManualToken(e.target.value)}
                placeholder="Fonzip access token (elle)"
                data-testid="fonzip-manual-token-input"
              />
              <Button size="sm" variant="outline" disabled={manualToken.length < 16 || busy === "tok"} onClick={() => act("tok", () => axios.post(`${API}/admin/fonzip/token`, { access_token: manualToken }, cfg), (d) => d.api_ok ? "Token geçerli, API bağlandı" : `Token kaydedildi ama API hatası: ${d.error}`)} data-testid="fonzip-save-token">
                Kaydet
              </Button>
            </div>
          )}
          <a href={info?.campaign_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid="fonzip-campaign-link">
            Kampanya sayfasını aç <ExternalLink className="w-3 h-3" />
          </a>
        </CardContent></Card>

        <Card><CardContent className="p-5 space-y-3">
          <h2 className="font-semibold">Akış Anahtarları</h2>
          {[["fonzip.guardian_enabled", "Koruyucu Aile → Fonzip", info?.guardian_enabled], ["fonzip.monthly_enabled", "Aylık Bağış → Fonzip", info?.monthly_enabled], ["fonzip.oneoff_enabled", "Tek Seferlik Kart → Fonzip", info?.oneoff_enabled]].map(([k, label, on]) => (
            <div key={k} className="flex items-center justify-between text-sm">
              <span>{label}</span>
              <Button size="sm" variant={on ? "default" : "outline"} disabled={busy === k} onClick={() => toggle(k, !on)} data-testid={`fonzip-toggle-${k.split(".")[1]}`}>
                {on ? "Açık" : "Kapalı"}
              </Button>
            </div>
          ))}
          <div className="grid grid-cols-2 gap-2 pt-2 text-center">
            {[["Aktif Fonzip KA", info?.counts?.active_fonzip_guardians], ["Bekleyen niyet", info?.counts?.pending_intents], ["Eşleşen", info?.counts?.matched], ["Eşleşmeyen", info?.counts?.unmatched], ...(info?.counts?.pending_fetch ? [["Detayı çekilemeyen", info.counts.pending_fetch]] : []), ...(info?.counts?.ignored_other ? [["KAHU dışı (yok sayıldı)", info.counts.ignored_other]] : [])].map(([l, v]) => (
              <div key={l} className="rounded-lg bg-muted/50 p-2"><div className="text-lg font-bold">{v ?? 0}</div><div className="text-[11px] text-muted-foreground">{l}</div></div>
            ))}
          </div>
          <Button size="sm" variant="outline" disabled={!info?.api_ok || busy === "sync"} onClick={() => act("sync", () => axios.post(`${API}/admin/fonzip/sync?days=30`, {}, cfg), (d) => `${d.synced} bağış senkronlandı`)} data-testid="fonzip-sync">
            <RefreshCw className="w-4 h-4 mr-1" /> Son 30 günü Fonzip'ten çek
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            disabled={busy === "purge"}
            onClick={async () => {
              try {
                const dry = await axios.post(`${API}/admin/fonzip/purge-test-data`, { dry_run: true }, cfg);
                const w = dry.data.would_delete;
                const total = Object.values(w).reduce((a, b) => a + b, 0);
                if (!total) return toast.info("Silinecek Fonzip kaydı yok");
                if (!window.confirm(`Silinecek: ${w.guardian_families} koruyucu aile, ${w.donations} bağış, ${w.fonzip_donations} Fonzip eşleşme kaydı.\n\nBu işlem geri alınamaz. Devam edilsin mi?`)) return;
                await act("purge", () => axios.post(`${API}/admin/fonzip/purge-test-data`, { dry_run: false, confirm: "FONZIP-TEST-SIL" }, cfg), (d) => `${d.total} Fonzip test kaydı silindi`);
              } catch (e) {
                toast.error(e.response?.data?.detail || "Temizlik başarısız");
              }
            }}
            data-testid="fonzip-purge-test"
          >
            <Trash2 className="w-4 h-4 mr-1" /> Fonzip test kayıtlarını temizle
          </Button>
        </CardContent></Card>
      </div>

      <Card><CardContent className="p-5 space-y-3">
        <h2 className="font-semibold flex items-center gap-2">Eşleşmeyen Bağışlar <Badge variant="secondary">{unmatched.length}</Badge></h2>
        {unmatched.length === 0 && <p className="text-sm text-muted-foreground" data-testid="fonzip-no-unmatched">Eşleşmeyen Fonzip bağışı yok.</p>}
        {unmatched.map((r) => <UnmatchedRow key={r.fonzip_id} row={r} guardians={guardians} onMatched={load} />)}
      </CardContent></Card>

      <Card><CardContent className="p-5 space-y-2">
        <h2 className="font-semibold">Son Fonzip Tahsilatları</h2>
        {matched.length === 0 && <p className="text-sm text-muted-foreground">Henüz eşleşmiş tahsilat yok.</p>}
        {matched.slice(0, 30).map((r) => (
          <div key={r.fonzip_id} className="flex flex-wrap justify-between gap-2 text-sm border-b last:border-0 py-2" data-testid={`fonzip-matched-${r.fonzip_id}`}>
            <span>{r.donor_name} <span className="text-muted-foreground">· {r.donor_email}</span></span>
            <span>{Number(r.amount || 0).toLocaleString("tr-TR")} {r.currency} · <Badge variant="outline">{r.match.matched === "guardian" ? "Koruyucu Aile" : "Bağış"} · {r.match.method}</Badge></span>
          </div>
        ))}
      </CardContent></Card>
    </div>
  );
}
